// screens/AdminTaxComplianceScreen.js
// ============================================================================
// Admin dashboard for IRS 1099-MISC compliance.
//
// Lists all users with prize earnings + tax-document status. Lets admin:
// - Filter by doc status (missing, has W-9, has W-8BEN, etc.)
// - Sort by earnings / recent / name
// - View tax doc details (audit-logged)
// - Mark docs as verified or needing update
// - Export year-end report (Phase 1: CSV; Phase 2: hooks to filing service)
//
// NEVER displays SSN/TIN — only the last 4 + service portal link. The full
// SSN lives on Avalara 1099 (formerly Track1099), not in Firestore.
// ============================================================================

import React, { useCallback, useEffect, useState } from 'react';
import {
  SafeAreaView, View, Text, StyleSheet, TouchableOpacity, Pressable,
  ActivityIndicator, ScrollView, Modal, Platform,
} from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { getAuth, onAuthStateChanged } from 'firebase/auth';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../../firebaseConfig';
import { httpsCallable, getFunctions } from 'firebase/functions';

import Header from '../components/Header';
import Footer from '../components/Footer';
import NavBar from '../components/NavBar';

import { TAX_DOC_STATUS_LABELS, THRESHOLD_CENTS } from '../constants/taxConstants';

const COLORS = {
  bg: '#000000',
  card: '#111111',
  cardAlt: '#0a0a0a',
  border: '#1f1f1f',
  borderLight: '#2a2a2a',
  primary: '#FCD000',
  success: '#22c55e',
  warning: '#f59e0b',
  danger: '#ef4444',
  info: '#3b82f6',
  text: '#ffffff',
  textMuted: '#888888',
  textDim: '#555555',
};

const fmtCents = (cents) => {
  const n = Number(cents) || 0;
  return `$${(n / 100).toFixed(2)}`;
};
const fmtDate = (ms) => {
  if (!ms) return '—';
  return new Date(ms).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
};

const FILTER_OPTIONS = [
  { id: 'all',              displayName: 'All users with earnings' },
  { id: 'over_600_no_doc',  displayName: 'Over $600 — no doc' },
  { id: 'has_w9',           displayName: 'Has W-9 (US)' },
  { id: 'has_w8ben',        displayName: 'Has W-8BEN (non-US)' },
  { id: 'has_any_doc',      displayName: 'Has any doc' },
];

const SORT_OPTIONS = [
  { id: 'earnings_desc', displayName: 'Highest earnings' },
  { id: 'recent_doc',    displayName: 'Most recent doc' },
  { id: 'name_asc',      displayName: 'Name A→Z' },
];

export default function AdminTaxComplianceScreen({ navigation }) {
  const auth = getAuth();
  const functions = getFunctions();

  const [user, setUser] = useState(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [guardLoading, setGuardLoading] = useState(true);

  const [rows, setRows] = useState([]);
  const [currentYear, setCurrentYear] = useState(null);
  const [loading, setLoading] = useState(false);
  const [errorText, setErrorText] = useState(null);
  const [statusText, setStatusText] = useState(null);
  const [filter, setFilter] = useState('all');
  const [sortBy, setSortBy] = useState('earnings_desc');

  // Detail modal
  const [detailUid, setDetailUid] = useState(null);
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);

  // ----- Admin guard -----
  useEffect(() => {
    const unsub = onAuthStateChanged(auth, async (u) => {
      if (!u) {
        setUser(null); setIsAdmin(false); setGuardLoading(false);
        navigation.replace('Landing');
        return;
      }
      setUser(u);
      try {
        const token = await u.getIdTokenResult(true);
        const claimAdmin = token?.claims?.role === 'admin';
        const snap = await getDoc(doc(db, 'users', u.uid));
        const docAdmin = snap.exists() && snap.data()?.role === 'admin';
        setIsAdmin(claimAdmin || docAdmin);
      } catch {
        setIsAdmin(false);
      } finally {
        setGuardLoading(false);
      }
    });
    return unsub;
  }, [auth, navigation]);

  // ----- List fetch -----
  const fetchList = useCallback(async () => {
    setLoading(true);
    setErrorText(null);
    try {
      const fn = httpsCallable(functions, 'adminListTaxCompliance');
      const result = await fn({ filter, sortBy, limit: 200 });
      setRows(result.data?.rows || []);
      setCurrentYear(result.data?.currentYear || null);
    } catch (e) {
      console.error('[admin-tax] list fetch failed:', e);
      setErrorText(`Failed to load: ${e.message}`);
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [functions, filter, sortBy]);

  useEffect(() => {
    if (isAdmin) fetchList();
  }, [isAdmin, fetchList]);

  // ----- Detail view -----
  const openDetail = async (uid) => {
    setDetailUid(uid);
    setDetail(null);
    setDetailLoading(true);
    try {
      const fn = httpsCallable(functions, 'adminViewTaxDocument');
      const result = await fn({ uid });
      setDetail(result.data);
    } catch (e) {
      console.error('[admin-tax] detail fetch failed:', e);
      setErrorText(`Failed to load detail: ${e.message}`);
      setDetailUid(null);
    } finally {
      setDetailLoading(false);
    }
  };

  const markVerified = async (docId, verified) => {
    setErrorText(null);
    try {
      const fn = httpsCallable(functions, 'adminMarkTaxDocVerified');
      await fn({ docId, verified, notes: null });
      setStatusText(`Marked ${verified ? 'verified' : 'needs update'}.`);
      setDetailUid(null);
      await fetchList();
    } catch (e) {
      console.error('[admin-tax] mark failed:', e);
      setErrorText(`Failed: ${e.message}`);
    }
  };

  // ----- CSV export -----
  const exportCsv = () => {
    if (rows.length === 0) return;
    const header = ['UID', 'Name', 'Email', 'Country', 'CurrentYearCents', 'PriorYearsAtRisk', 'DocFormType', 'DocStatus', 'DocSubmittedAt'];
    const csvRows = [header.join(',')];
    rows.forEach(r => {
      const priorYearsStr = r.priorYearTotals.map(p => `${p.year}:${p.totalPrizeCents}`).join('|');
      csvRows.push([
        r.uid,
        `"${(r.displayName || '').replace(/"/g, '""')}"`,
        `"${(r.email || '').replace(/"/g, '""')}"`,
        r.country || '',
        r.currentYearCents,
        priorYearsStr,
        r.taxDocFormType || '',
        r.taxDocStatus || '',
        r.taxDocSubmittedAt || '',
      ].join(','));
    });
    const csv = csvRows.join('\n');
    if (Platform.OS === 'web') {
      const blob = new Blob([csv], { type: 'text/csv' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `tax_compliance_${new Date().toISOString().slice(0,10)}.csv`;
      a.click();
      URL.revokeObjectURL(url);
    }
  };

  // ----- Render guards -----
  if (guardLoading) {
    return (
      <SafeAreaView style={styles.safeArea}>
        <View style={styles.center}><ActivityIndicator color={COLORS.primary} /></View>
      </SafeAreaView>
    );
  }
  if (!isAdmin) {
    return (
      <SafeAreaView style={styles.safeArea}>
        <View style={styles.center}>
          <Ionicons name="lock-closed" size={48} color={COLORS.danger} />
          <Text style={styles.warn}>Admin access required.</Text>
        </View>
      </SafeAreaView>
    );
  }

  // ----- Summary tiles -----
  const totalRows = rows.length;
  const over600NoDoc = rows.filter(r =>
    (r.priorYearTotals.length > 0 || r.currentYearCents >= THRESHOLD_CENTS) && !r.taxDocStatus
  ).length;
  const hasDoc = rows.filter(r => !!r.taxDocStatus).length;

  return (
    <SafeAreaView style={styles.safeArea}>
      <Header navigation={navigation} />
      <NavBar navigation={navigation} />
      <ScrollView contentContainerStyle={styles.scroll}>
        <View style={styles.titleRow}>
          <View style={{ flex: 1 }}>
            <Text style={styles.title}>Tax Compliance</Text>
            <Text style={styles.subtitle}>1099-MISC reporting status · {currentYear || 'current year'}</Text>
          </View>
          <Pressable
            onPress={exportCsv}
            disabled={rows.length === 0}
            style={({ pressed }) => [styles.exportBtn, pressed && rows.length > 0 && { opacity: 0.85 }, rows.length === 0 && { opacity: 0.4 }]}
          >
            <Ionicons name="download-outline" size={14} color={COLORS.bg} />
            <Text style={styles.exportBtnText}>Export CSV</Text>
          </Pressable>
        </View>

        {errorText && (
          <View style={styles.errorBanner}>
            <Ionicons name="alert-circle" size={18} color={COLORS.danger} />
            <Text style={styles.errorText}>{errorText}</Text>
          </View>
        )}
        {statusText && !errorText && (
          <View style={styles.statusBanner}>
            <Ionicons name="checkmark-circle" size={18} color={COLORS.success} />
            <Text style={styles.statusBannerText}>{statusText}</Text>
          </View>
        )}

        {/* Summary tiles */}
        <View style={styles.summaryRow}>
          <View style={styles.summaryTile}>
            <Text style={styles.summaryLabel}>Total users</Text>
            <Text style={styles.summaryValue}>{totalRows}</Text>
          </View>
          <View style={styles.summaryTile}>
            <Text style={styles.summaryLabel}>Has doc</Text>
            <Text style={[styles.summaryValue, { color: COLORS.success }]}>{hasDoc}</Text>
          </View>
          <View style={styles.summaryTile}>
            <Text style={styles.summaryLabel}>Over $600, no doc</Text>
            <Text style={[styles.summaryValue, { color: over600NoDoc > 0 ? COLORS.danger : COLORS.success }]}>{over600NoDoc}</Text>
          </View>
        </View>

        {/* Filters */}
        <View style={styles.filterRow}>
          <View style={styles.filterCell}>
            <Text style={styles.filterLabel}>Filter</Text>
            {Platform.OS === 'web' ? (
              <select value={filter} onChange={(e) => setFilter(e.target.value)} style={webSelectStyle}>
                {FILTER_OPTIONS.map(o => <option key={o.id} value={o.id}>{o.displayName}</option>)}
              </select>
            ) : (
              <Pressable onPress={() => {
                const idx = FILTER_OPTIONS.findIndex(o => o.id === filter);
                setFilter(FILTER_OPTIONS[(idx + 1) % FILTER_OPTIONS.length].id);
              }} style={styles.cycleBtn}>
                <Text style={{ color: COLORS.text }}>{FILTER_OPTIONS.find(o => o.id === filter)?.displayName}</Text>
              </Pressable>
            )}
          </View>
          <View style={styles.filterCell}>
            <Text style={styles.filterLabel}>Sort by</Text>
            {Platform.OS === 'web' ? (
              <select value={sortBy} onChange={(e) => setSortBy(e.target.value)} style={webSelectStyle}>
                {SORT_OPTIONS.map(o => <option key={o.id} value={o.id}>{o.displayName}</option>)}
              </select>
            ) : (
              <Pressable onPress={() => {
                const idx = SORT_OPTIONS.findIndex(o => o.id === sortBy);
                setSortBy(SORT_OPTIONS[(idx + 1) % SORT_OPTIONS.length].id);
              }} style={styles.cycleBtn}>
                <Text style={{ color: COLORS.text }}>{SORT_OPTIONS.find(o => o.id === sortBy)?.displayName}</Text>
              </Pressable>
            )}
          </View>
        </View>

        {loading ? (
          <View style={styles.card}>
            <ActivityIndicator color={COLORS.primary} />
            <Text style={styles.dim}>Loading…</Text>
          </View>
        ) : rows.length === 0 ? (
          <View style={styles.card}>
            <Text style={styles.dim}>No users match the current filter.</Text>
          </View>
        ) : (
          <View style={styles.tableCard}>
            <View style={styles.tableHeaderRow}>
              <Text style={[styles.tableHeader, { flex: 2.5 }]}>User</Text>
              <Text style={[styles.tableHeader, { flex: 1, textAlign: 'right' }]}>YTD</Text>
              <Text style={[styles.tableHeader, { flex: 1.5 }]}>Prior owed</Text>
              <Text style={[styles.tableHeader, { flex: 1 }]}>Form</Text>
              <Text style={[styles.tableHeader, { flex: 1.3 }]}>Status</Text>
              <Text style={[styles.tableHeader, { flex: 1.2, textAlign: 'right' }]}>Action</Text>
            </View>
            {rows.map(r => (
              <View key={r.uid} style={styles.tableRow}>
                <View style={{ flex: 2.5 }}>
                  <Text style={styles.cellPrimary} numberOfLines={1}>{r.displayName || r.uid}</Text>
                  <Text style={styles.cellSecondary} numberOfLines={1}>{r.email || '—'}{r.country ? ` · ${r.country}` : ''}</Text>
                </View>
                <Text style={[styles.cellAmount, { flex: 1 }]}>{fmtCents(r.currentYearCents)}</Text>
                <View style={{ flex: 1.5 }}>
                  {r.priorYearTotals.length > 0 ? (
                    r.priorYearTotals.map(py => (
                      <Text key={py.year} style={styles.cellSecondary}>{py.year}: {fmtCents(py.totalPrizeCents)}</Text>
                    ))
                  ) : (
                    <Text style={styles.cellSecondary}>—</Text>
                  )}
                </View>
                <Text style={[styles.cellPrimary, { flex: 1 }]}>{r.taxDocFormType || '—'}</Text>
                <View style={{ flex: 1.3 }}>
                  {r.taxDocStatus ? (
                    <View style={[styles.statusChip, { borderColor: TAX_DOC_STATUS_LABELS[r.taxDocStatus]?.color || COLORS.textMuted }]}>
                      <Text style={[styles.statusChipText, { color: TAX_DOC_STATUS_LABELS[r.taxDocStatus]?.color || COLORS.textMuted }]} numberOfLines={1}>
                        {TAX_DOC_STATUS_LABELS[r.taxDocStatus]?.displayName || r.taxDocStatus}
                      </Text>
                    </View>
                  ) : (
                    <Text style={styles.cellSecondaryWarn}>No doc</Text>
                  )}
                </View>
                <View style={[styles.actionsCell, { flex: 1.2 }]}>
                  {r.taxDocStatus ? (
                    <Pressable onPress={() => openDetail(r.uid)} style={({ pressed }) => [styles.actionBtn, pressed && { opacity: 0.85 }]}>
                      <Text style={styles.actionBtnText}>View</Text>
                    </Pressable>
                  ) : (
                    <Text style={styles.cellSecondary}>—</Text>
                  )}
                </View>
              </View>
            ))}
          </View>
        )}
      </ScrollView>

      {/* Detail modal */}
      <Modal visible={!!detailUid} transparent animationType="fade" onRequestClose={() => setDetailUid(null)}>
        <View style={styles.modalOverlay}>
          <View style={styles.modalCard}>
            <View style={styles.modalHeader}>
              <Text style={styles.modalTitle}>Tax Document Detail</Text>
              <Pressable onPress={() => setDetailUid(null)}>
                <Ionicons name="close" size={20} color={COLORS.text} />
              </Pressable>
            </View>

            {detailLoading ? (
              <ActivityIndicator color={COLORS.primary} />
            ) : detail ? (
              <ScrollView style={{ maxHeight: 460 }}>
                <DetailRow label="User UID" value={detail.uid} />
                <DetailRow label="Form type" value={detail.formType?.toUpperCase()} />
                <DetailRow label="Status" value={TAX_DOC_STATUS_LABELS[detail.status]?.displayName || detail.status} />
                <DetailRow label="Filing service" value={detail.filingService || '—'} />
                <DetailRow label="Service vendor ID" value={detail.serviceVendorId || '—'} />
                <DetailRow label="Legal name" value={detail.legalName || '—'} />
                <DetailRow label="Country" value={detail.country || '—'} />
                <DetailRow label="TIN ending in" value={detail.tinLast4 ? `****${detail.tinLast4}` : '—'} />
                <DetailRow label="Submitted" value={fmtDate(detail.formSubmittedAt)} />
                <DetailRow label="Year-end filed" value={detail.yearEndFiledFor ? `${detail.yearEndFiledFor} (${detail.yearEndConfirmationId})` : 'Not yet'} />

                <Text style={[styles.dim, { marginTop: 12, fontSize: 11, lineHeight: 16 }]}>
                  Full SSN/TIN lives on Avalara, not on CritCash. Click "View in Avalara" below to see complete details in the service's secure portal.
                  Your view of this document was logged to admin_audit_log.
                </Text>

                {detail.servicePortalUrl && Platform.OS === 'web' && (
                  <Pressable
                    onPress={() => window.open(detail.servicePortalUrl, '_blank', 'noopener,noreferrer')}
                    style={({ pressed }) => [styles.secondaryBtn, pressed && { opacity: 0.85 }]}
                  >
                    <Ionicons name="open-outline" size={14} color={COLORS.text} />
                    <Text style={styles.secondaryBtnText}>View in Avalara</Text>
                  </Pressable>
                )}

                <View style={styles.modalActions}>
                  <Pressable onPress={() => markVerified(detail.docId, false)} style={({ pressed }) => [styles.modalBtnWarn, pressed && { opacity: 0.85 }]}>
                    <Text style={styles.modalBtnWarnText}>Needs Update</Text>
                  </Pressable>
                  <Pressable onPress={() => markVerified(detail.docId, true)} style={({ pressed }) => [styles.modalBtnPrimary, pressed && { opacity: 0.85 }]}>
                    <Text style={styles.modalBtnPrimaryText}>Mark Verified</Text>
                  </Pressable>
                </View>
              </ScrollView>
            ) : (
              <Text style={styles.dim}>No detail loaded.</Text>
            )}
          </View>
        </View>
      </Modal>

      <Footer navigation={navigation} />
    </SafeAreaView>
  );
}

function DetailRow({ label, value }) {
  return (
    <View style={styles.detailRow}>
      <Text style={styles.detailLabel}>{label}</Text>
      <Text style={styles.detailValue} selectable>{value || '—'}</Text>
    </View>
  );
}

const webSelectStyle = {
  backgroundColor: '#0a0a0a',
  color: '#ffffff',
  border: '1px solid #1f1f1f',
  borderRadius: 6,
  padding: '8px 10px',
  fontSize: 13,
  outline: 'none',
  cursor: 'pointer',
};

const styles = StyleSheet.create({
  safeArea: { flex: 1, backgroundColor: COLORS.bg },
  scroll: { padding: 16, paddingBottom: 80 },
  center: { flex: 1, justifyContent: 'center', alignItems: 'center', backgroundColor: COLORS.bg, padding: 24 },
  dim: { color: COLORS.textMuted, fontSize: 13, marginTop: 8 },
  warn: { color: COLORS.danger, marginTop: 12, fontSize: 16, fontWeight: '600' },

  titleRow: { flexDirection: 'row', alignItems: 'center', marginBottom: 16, gap: 12 },
  title: { color: COLORS.text, fontSize: 22, fontWeight: '700' },
  subtitle: { color: COLORS.textMuted, fontSize: 13, marginTop: 2 },

  exportBtn: {
    flexDirection: 'row', alignItems: 'center', gap: 6,
    backgroundColor: COLORS.primary, paddingVertical: 10, paddingHorizontal: 14, borderRadius: 6,
  },
  exportBtnText: { color: COLORS.bg, fontSize: 13, fontWeight: '700' },

  errorBanner: {
    flexDirection: 'row', alignItems: 'center', gap: 8,
    backgroundColor: 'rgba(239,68,68,0.10)', borderColor: COLORS.danger, borderWidth: 1,
    borderRadius: 6, padding: 12, marginBottom: 12,
  },
  errorText: { color: COLORS.danger, fontSize: 13, flex: 1 },

  statusBanner: {
    flexDirection: 'row', alignItems: 'center', gap: 8,
    backgroundColor: 'rgba(34,197,94,0.08)', borderColor: COLORS.success, borderWidth: 1,
    borderRadius: 6, padding: 12, marginBottom: 12,
  },
  statusBannerText: { color: COLORS.success, fontSize: 13, flex: 1 },

  summaryRow: { flexDirection: 'row', gap: 8, marginBottom: 12 },
  summaryTile: {
    flex: 1,
    backgroundColor: COLORS.cardAlt, borderColor: COLORS.borderLight, borderWidth: 1,
    borderRadius: 6, padding: 10,
  },
  summaryLabel: { color: COLORS.textMuted, fontSize: 11, fontWeight: '600' },
  summaryValue: { color: COLORS.text, fontSize: 20, fontWeight: '700', marginTop: 2 },

  filterRow: { flexDirection: 'row', gap: 12, marginBottom: 12 },
  filterCell: { flex: 1 },
  filterLabel: { color: COLORS.textMuted, fontSize: 11, fontWeight: '600', marginBottom: 4 },
  cycleBtn: {
    backgroundColor: COLORS.cardAlt, borderColor: COLORS.border, borderWidth: 1,
    borderRadius: 6, padding: 8,
  },

  card: {
    backgroundColor: COLORS.card, borderColor: COLORS.border, borderWidth: 1,
    borderRadius: 8, padding: 16, marginBottom: 16, alignItems: 'center',
  },
  tableCard: {
    backgroundColor: COLORS.card, borderColor: COLORS.border, borderWidth: 1,
    borderRadius: 8, marginBottom: 16, overflow: 'hidden',
  },
  tableHeaderRow: {
    flexDirection: 'row', gap: 8, paddingHorizontal: 12, paddingVertical: 10,
    backgroundColor: COLORS.cardAlt, borderBottomColor: COLORS.border, borderBottomWidth: 1,
  },
  tableHeader: { color: COLORS.textMuted, fontSize: 11, fontWeight: '600', letterSpacing: 0.4 },
  tableRow: {
    flexDirection: 'row', gap: 8, paddingHorizontal: 12, paddingVertical: 12,
    borderBottomColor: COLORS.border, borderBottomWidth: 1, alignItems: 'center',
  },
  cellPrimary: { color: COLORS.text, fontSize: 13 },
  cellSecondary: { color: COLORS.textMuted, fontSize: 11, marginTop: 2 },
  cellSecondaryWarn: { color: COLORS.warning, fontSize: 12 },
  cellAmount: { color: COLORS.text, fontSize: 14, fontWeight: '700', textAlign: 'right', fontVariant: ['tabular-nums'] },

  statusChip: { paddingHorizontal: 6, paddingVertical: 2, borderRadius: 4, borderWidth: 1, alignSelf: 'flex-start' },
  statusChipText: { fontSize: 10, fontWeight: '600' },

  actionsCell: { flexDirection: 'row', gap: 6, justifyContent: 'flex-end' },
  actionBtn: {
    paddingHorizontal: 10, paddingVertical: 5,
    backgroundColor: COLORS.cardAlt, borderColor: COLORS.border, borderWidth: 1, borderRadius: 4,
  },
  actionBtnText: { color: COLORS.text, fontSize: 11 },

  modalOverlay: {
    flex: 1, backgroundColor: 'rgba(0,0,0,0.7)',
    justifyContent: 'center', alignItems: 'center', padding: 16,
  },
  modalCard: {
    backgroundColor: COLORS.card, borderColor: COLORS.borderLight, borderWidth: 1,
    borderRadius: 8, padding: 20, width: '100%', maxWidth: 560, maxHeight: '90%',
  },
  modalHeader: { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 },
  modalTitle: { color: COLORS.text, fontSize: 18, fontWeight: '700' },

  detailRow: { flexDirection: 'row', justifyContent: 'space-between', paddingVertical: 6, borderBottomColor: COLORS.border, borderBottomWidth: 1, alignItems: 'flex-start' },
  detailLabel: { color: COLORS.textMuted, fontSize: 12, flex: 1 },
  detailValue: { color: COLORS.text, fontSize: 12, fontWeight: '600', flex: 1.5, textAlign: 'right' },

  modalActions: { flexDirection: 'row', gap: 10, justifyContent: 'flex-end', marginTop: 16, borderTopColor: COLORS.border, borderTopWidth: 1, paddingTop: 12 },
  modalBtnPrimary: { backgroundColor: COLORS.success, paddingVertical: 8, paddingHorizontal: 14, borderRadius: 6 },
  modalBtnPrimaryText: { color: COLORS.bg, fontSize: 13, fontWeight: '700' },
  modalBtnWarn: { backgroundColor: COLORS.cardAlt, borderColor: COLORS.warning, borderWidth: 1, paddingVertical: 8, paddingHorizontal: 14, borderRadius: 6 },
  modalBtnWarnText: { color: COLORS.warning, fontSize: 13, fontWeight: '600' },

  secondaryBtn: {
    flexDirection: 'row', alignItems: 'center', justifyContent: 'center', gap: 6,
    backgroundColor: COLORS.cardAlt, borderColor: COLORS.border, borderWidth: 1,
    paddingVertical: 10, paddingHorizontal: 18, borderRadius: 6, marginTop: 12,
  },
  secondaryBtnText: { color: COLORS.text, fontSize: 13, fontWeight: '600' },
});
